import React, { useState, useEffect } from 'react'; // eslint-disable-line no-unused-vars
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Typography,
  LinearProgress,
  ThemeProvider,
  createTheme,
  type ThemeOptions,
} from '@mui/material';
import { useAuth0 } from '@auth0/auth0-react';
import { getUserProfile, saveUserProfile } from '../utilities/actions';
import * as consts from '../utilities/consts';
import { RequiredTextField } from './requiredTextField';
import { light } from '../styles';
import { DefaultUserProfile, type UserProfile } from '../types';

const theme = createTheme(light as ThemeOptions);

const requiredTest =
  (label: string) =>
  (value: string): string =>
    (value ?? '').trim() === '' ? `${label} is required` : '';

export const ProfileNameDialog = (): React.JSX.Element | null => {
  const { isLoading, isAuthenticated } = useAuth0();
  const [userProfile, setUserProfile] = useState<UserProfile>({ ...DefaultUserProfile });
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');
  const [open, setOpen] = useState<boolean>(false);
  const [busy, setBusy] = useState<boolean>(false);

  useEffect(() => {
    const fetchData = async () => {
      const userProfileRequest = await getUserProfile();
      if (!userProfileRequest || !userProfileRequest.id) return;
      const missingName =
        (userProfileRequest.firstName ?? '').trim() === '' ||
        (userProfileRequest.lastName ?? '').trim() === '';
      if (missingName) {
        setUserProfile(userProfileRequest);
        setFirstName(userProfileRequest.firstName ?? '');
        setLastName(userProfileRequest.lastName ?? '');
        setOpen(true);
      }
    };
    if (!isLoading && isAuthenticated) fetchData();
  }, [isLoading, isAuthenticated]);

  const isValid = firstName.trim().length > 0 && lastName.trim().length > 0;

  const handleSubmit = async () => {
    setBusy(true);
    const updatedUserProfile: UserProfile = {
      ...userProfile,
      firstName: firstName.trim(),
      lastName: lastName.trim(),
    };
    await saveUserProfile(updatedUserProfile);
    window.dispatchEvent(
      new CustomEvent<UserProfile>(consts.EVENT_USERPROFILE_UPDATED, {
        detail: updatedUserProfile,
      }),
    );
    setBusy(false);
    setOpen(false);
  };

  if (!open) return null;

  return (
    <ThemeProvider theme={theme}>
      <Dialog open={open} disableEscapeKeyDown={true} fullWidth={true} maxWidth="xs">
        <DialogTitle>Complete Your Profile</DialogTitle>
        <DialogContent>
          <Grid size={{ xs: 12 }} container spacing={2}>
            <Grid size={{ xs: 12 }}>
              <Typography variant="body1">
                It looks like you haven't provided your first and last name. We invite you to add
                those now, so we can better stay in touch!
              </Typography>
            </Grid>
            <Grid size={{ xs: 12 }}>
              <RequiredTextField
                disabled={busy}
                label="First Name"
                autoComplete="given-name"
                value={firstName}
                test={requiredTest('First name')}
                onChange={(event) => setFirstName(event.target.value)}
              />
            </Grid>
            <Grid size={{ xs: 12 }}>
              <RequiredTextField
                disabled={busy}
                label="Last Name"
                autoComplete="family-name"
                value={lastName}
                test={requiredTest('Last name')}
                onChange={(event) => setLastName(event.target.value)}
              />
            </Grid>
            {busy && (
              <Grid size={{ xs: 12 }}>
                <LinearProgress />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleSubmit} variant="contained" disabled={busy || !isValid}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};
